import type { ReactNode } from 'react';
import { formatCompact } from './TrendChart';

interface StatTileProps {
  /**
   * Usually a string. Widened to a node so a tile can carry a live dot beside its label without a
   * second copy of this markup existing elsewhere — the presence strip is the only caller that
   * needs it, and a forked tile would drift from this one's padding and type scale.
   */
  label: ReactNode;
  /**
   * A number is abbreviated by {@link formatCompact}. A STRING is rendered verbatim — for values
   * that carry their own units and must not be abbreviated, money above all: "$1.2K" of MRR is not
   * a figure anyone can act on, and rounding revenue for width is the wrong trade.
   */
  value: number | string | null;
  /** Short clarifier, e.g. which provider supplied the number. */
  sub?: string;
  /** Reserved status colour, for frustration signals. Never a categorical series hue. */
  tone?: 'default' | 'warning' | 'critical';
}

/**
 * A headline number. Deliberately NOT a one-bar bar chart — a single current value is a
 * stat tile; a chart would add axes and ink around one number.
 */
export function StatTile({ label, value, sub, tone = 'default' }: StatTileProps) {
  const toneColor =
    tone === 'critical' ? 'var(--viz-critical)' : tone === 'warning' ? 'var(--viz-warning)' : 'var(--text-main)';

  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">{label}</div>
      {/* Proportional figures on purpose: tabular-nums looks loose at display size. */}
      <div className="stat-value" style={{ color: value === null ? 'var(--text-subtle)' : toneColor }}>
        {value === null ? '—' : typeof value === 'string' ? value : formatCompact(value)}
      </div>
      {sub && <div className="stat-sub t-caption">{sub}</div>}
    </div>
  );
}
